/**
 * Billing + Reports query hooks.
 * GET /api/billing?client_id=... — счётчик диалогов, подписка, сверхлимит, договор 50/50.
 * GET /api/reports?client_id=...  — сводка по воронке.
 *
 * КАНОН: имена полей = backend BillingResponse / ReportsResponse (прайс v3.1).
 * Моки повторяют форму бэка байт-в-байт, чтобы dev и прод совпадали.
 */

import { useQuery } from '@tanstack/react-query'
import { billingApi, reportsApi } from '@/api/client'
import type { Billing, Reports } from '@/types'

const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

// ---- Mock data — форма = backend BillingResponse ----

function mockBilling(clientId?: string | null): Billing {
  const now = new Date()
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)
  return {
    client_id: clientId ?? 'tenant-touchbrand',
    client_name: 'TouchBrand (demo)',
    period_start: firstDay.toISOString(),
    period_end: lastDay.toISOString(),
    dialogs_total: 134,
    dialogs_included: 100,
    dialogs_billable: 34,
    subscription_base: 5900,
    brackets: [
      { label: '101–300', from: 101, to: 300, rate: 15, count: 34, amount: 510 },
    ],
    overage_total: 510,
    invoice_total: 6410,
    payment_plan: 'split_50_50',
    paid_amount: 40000,
    contract_total: 80000,
  }
}

// ---- Mock data — форма = backend ReportsResponse ----

function mockReports(clientId?: string | null): Reports {
  void clientId
  return {
    total_deals: 15,
    won_deals: 4,
    lost_deals: 3,
    conversion_rate: 57.14,
    avg_deal_value: 86500,
    by_stage: [
      { stage_key: 'new_lead', stage_name: 'Новый лид', count: 2, value_total: 0 },
      { stage_key: 'conversation', stage_name: 'Разговор', count: 1, value_total: 80000 },
      { stage_key: 'proposal_sent', stage_name: 'КП отправлено', count: 1, value_total: 95000 },
      { stage_key: 'active', stage_name: 'Активный', count: 4, value_total: 346000 },
    ],
  }
}

// ---- Query keys ----

export const billingKeys = {
  billing: (clientId?: string | null) => ['billing', clientId ?? 'all'] as const,
  reports: (clientId?: string | null) => ['reports', clientId ?? 'all'] as const,
}

// ---- Hooks ----

export function useBilling(clientId?: string | null) {
  return useQuery<Billing>({
    queryKey: billingKeys.billing(clientId),
    queryFn: () => {
      if (USE_MOCK) return Promise.resolve(mockBilling(clientId))
      // Без клиента бэк отдаёт нулевую заглушку — экран осмыслен только с client_id
      if (!clientId) return Promise.reject(new Error('client_id required'))
      return billingApi.get(clientId)
    },
    staleTime: 60_000,
    // Don't fetch if no client selected
    enabled: USE_MOCK || !!clientId,
  })
}

export function useReports(clientId?: string | null) {
  return useQuery<Reports>({
    queryKey: billingKeys.reports(clientId),
    queryFn: () => {
      if (USE_MOCK) return Promise.resolve(mockReports(clientId))
      return reportsApi.get(clientId ?? undefined)
    },
    staleTime: 60_000,
  })
}
